/**
 * "DCO light": one HTML template with `{{placeholder}}` fields, filled in from
 * the rows of a spreadsheet, each row becoming an HTML creative on a line item.
 *
 * Creatives get predictable ids — `<lineItemId>_<uniqueId>` — so uploading
 * the same sheet again updates the creatives it made last time instead of
 * adding new ones.
 */
import { AssetsApi, isHtmlAsset } from '@/services/assets/assetsApi'
import { CreativesApi } from '@/services/creatives/creativesApi'
import type { Layout } from '@/services/sites/layoutsApi'
import type { SheetCell } from '@/utils/xlsx'
import { createZip, type ZipEntry } from '@/utils/zip'

const PLACEHOLDER_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g
const IMAGE_URL_PATTERN = /^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg)(\?\S*)?$/i
const HTML_MIME_TYPES = ['TEXT_HTML', 'TEXT_XHTML']

/** The distinct placeholder names in a template, in order of appearance. */
export function templatePlaceholders(html: string): string[] {
  return [...new Set([...html.matchAll(PLACEHOLDER_PATTERN)].map((m) => m[1]))]
}

/** The size an IAB `<meta name="ad.size" content="width=300,height=250">` declares. */
export function templateAdSize(html: string): { width: number; height: number } | null {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const content = doc.querySelector('meta[name="ad.size"]')?.getAttribute('content') ?? ''
  const width = Number(/width\s*=\s*(\d+)/i.exec(content)?.[1])
  const height = Number(/height\s*=\s*(\d+)/i.exec(content)?.[1])
  return width > 0 && height > 0 ? { width, height } : null
}

/**
 * The tag of the HTML slot of a layout that takes nothing but one HTML file,
 * or null when the layout has other components too.
 */
export function htmlOnlyLayoutTag(layout: Layout): string | null {
  const [component, ...rest] = layout.layoutComponents
  if (!component || rest.length > 0 || component.type !== 'ASSET') return null
  return component.mimeTypes?.some((m) => HTML_MIME_TYPES.includes(m)) ? component.tag : null
}

/** A unique id as it can appear in an object id: letters, digits, `_` and `-`. */
export function cleanUniqueId(value: string): string {
  return value
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function dcoCreativeId(lineItemId: string, uniqueId: string): string {
  return `${lineItemId}_${cleanUniqueId(uniqueId)}`
}

export function isImageUrl(text: string): boolean {
  return IMAGE_URL_PATTERN.test(text.trim())
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderTemplate(template: string, valueOf: (placeholder: string) => string): string {
  return template.replace(PLACEHOLDER_PATTERN, (_, name: string) => valueOf(name))
}

/** One spreadsheet row, ready to become a creative. */
export interface DcoRow {
  uniqueId: string
  creativeId: string
  name: string
  cells: SheetCell[]
}

/** Placeholder name → index of the spreadsheet column that fills it. */
export type PlaceholderColumns = Record<string, number>

/**
 * The creative as it will look, for previewing before anything is uploaded.
 * `imageSrc` supplies a displayable URL for each picture from the sheet.
 */
export function previewDcoHtml(
  template: string,
  columns: PlaceholderColumns,
  row: DcoRow,
  imageSrc: (image: File) => string
): string {
  return renderTemplate(template, (name) => {
    const cell = row.cells[columns[name]]
    if (!cell) return ''
    if (cell.image) return imageSrc(cell.image)
    return isImageUrl(cell.text) ? cell.text.trim() : escapeHtml(cell.text)
  })
}

/** A picture as a data URL, which a sandboxed preview frame can load. */
export function imageDataUrl(image: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(image)
  })
}

/** A picture's path inside the creative's zip: stable per column, e.g. `images/hero-image.png`. */
function imagePath(column: number, header: string, image: File): string {
  const slug = header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  const extension =
    image.name.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() ?? image.type.split(/[/+]/)[1] ?? 'png'
  return `images/${slug || `column-${column + 1}`}.${extension}`
}

async function fetchImage(url: string): Promise<File> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  const name = new URL(url).pathname.split('/').pop() || 'image'
  return new File([blob], name, { type: blob.type })
}

export interface DcoGenerateJob {
  lineItemId: string
  layoutId: string
  /** The layout's HTML slot. */
  htmlTag: string
  width: number
  height: number
  template: string
  headers: string[]
  columns: PlaceholderColumns
  row: DcoRow
}

export interface DcoGenerateResult {
  /** Image URLs the browser could not download, left pointing at their original host. */
  unreachableImages: string[]
}

/**
 * Creates or overwrites the creative for one row: saves the creative, then
 * uploads the filled-in HTML and the row's pictures as one zip into the
 * layout's HTML slot.
 *
 * A zip rather than a bare HTML file, because only zips go through the
 * server's HTML5 handling: each file becomes an asset of the creative, the
 * HTML gets a base href so the relative picture paths resolve, and its links
 * are picked up as click-through URLs that the ad server can track.
 *
 * Pictures embedded in the sheet always go in the zip. Image URLs are
 * downloaded and zipped too, but when the host refuses the browser (CORS)
 * the creative keeps the original URL and it is reported back.
 */
export async function generateDcoCreative(job: DcoGenerateJob): Promise<DcoGenerateResult> {
  const { row } = job
  const base = {
    name: row.name,
    lineItem: job.lineItemId,
    layout: job.layoutId,
    width: job.width,
    height: job.height,
  }
  // Assets can only be uploaded to a creative that exists
  await CreativesApi.update(row.creativeId, base)

  const unreachableImages: string[] = []
  const entries: ZipEntry[] = []
  const imagePaths = new Map<number, string>()
  for (const column of new Set(Object.values(job.columns))) {
    const cell = row.cells[column]
    if (!cell) continue
    let file = cell.image ?? null
    if (!file && isImageUrl(cell.text)) {
      try {
        file = await fetchImage(cell.text.trim())
      } catch {
        unreachableImages.push(cell.text.trim())
      }
    }
    if (!file) continue
    const path = imagePath(column, job.headers[column] ?? '', file)
    entries.push({ name: path, content: new Uint8Array(await file.arrayBuffer()) })
    imagePaths.set(column, path)
  }

  const html = renderTemplate(job.template, (name) => {
    const column = job.columns[name]
    const cell = row.cells[column]
    if (!cell) return ''
    const path = imagePaths.get(column)
    if (path) return path
    return isImageUrl(cell.text) ? cell.text.trim() : escapeHtml(cell.text)
  })
  entries.unshift({ name: 'index.html', content: html })

  const zip = new File([createZip(entries)], `${cleanUniqueId(row.uniqueId)}.zip`, {
    type: 'application/zip',
  })
  const assets = await AssetsApi.uploadZipForCreative(row.creativeId, `${row.creativeId}_html`, zip)
  const primary =
    assets.find((a) => a.primaryHtmlAsset) ?? assets.find((a) => isHtmlAsset(a.mimeType))
  if (!primary) throw new Error('The server found no HTML file in the uploaded zip')

  await CreativesApi.update(row.creativeId, {
    ...base,
    layoutParameters: { [job.htmlTag]: { id: primary.id } },
  })
  return { unreachableImages }
}
